import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Button,
  TextField,
  MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { authGet, authPost, authPostBlob } from '../../lib/api';
import SavedColumnViewControls, { useSavedColumnView } from '../../components/SavedColumnViewControls';
import { formatDate, todayDate } from '../../lib/dateFormat';
import {
  buildAnnualReportTypesUrl,
  buildAnnualReportPreviewPayload,
  normalizeAnnualReportTypes
} from './annualReportForm';

function todayInput() {
  return todayDate();
}

function yearStartInput() {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
}

function formatTimeLabel(time24) {
  if (!time24) return '';
  const [hourStr, minute] = String(time24).slice(0, 5).split(':');
  const hour = Number(hourStr);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute} ${suffix}`;
}

export default function ReportsPage() {
  const [startDate, setStartDate] = useState(yearStartInput());
  const [endDate, setEndDate] = useState(todayInput());
  const [selectedClass, setSelectedClass] = useState('');
  const [loading, setLoading] = useState(false);
  const [annualStartDate, setAnnualStartDate] = useState(yearStartInput());
  const [annualEndDate, setAnnualEndDate] = useState(todayInput());
  const [annualProgramTypes, setAnnualProgramTypes] = useState([]);
  const [selectedProgramType, setSelectedProgramType] = useState('');
  const [annualTypesLoading, setAnnualTypesLoading] = useState(false);
  const [annualPreviewLoading, setAnnualPreviewLoading] = useState(false);
  const [annualPreview, setAnnualPreview] = useState(null);
  const [annualError, setAnnualError] = useState('');
  const [spanishCourses, setSpanishCourses] = useState('');
  const [significantSubstanceAbusePercent, setSignificantSubstanceAbusePercent] = useState('');

  const [reportData, setReportData] = useState({
    instructorByClasses: [],
    studentsPerClass: [],
    annual: {
      total_classes: 0,
      total_student_seats: 0,
      class_types_used: 0,
      instructors_used: 0
    },
    classDetails: []
  });

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await authGet(
        `/api/reports/summary?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}`
      );
      setReportData(data);
    } catch (err) {
      console.error('Load reports error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAnnualReportTypes = useCallback(async () => {
    setAnnualTypesLoading(true);
    setAnnualError('');
    try {
      const data = await authGet(buildAnnualReportTypesUrl());
      const types = normalizeAnnualReportTypes(data);
      setAnnualProgramTypes(types);
      if (!types.some((type) => type.id === selectedProgramType)) {
        setSelectedProgramType('');
      }
      setAnnualPreview(null);
    } catch (err) {
      console.error('Load annual report types error:', err);
      setAnnualProgramTypes([]);
      setSelectedProgramType('');
      setAnnualPreview(null);
      setAnnualError(err.message || 'Unable to load program types');
    } finally {
      setAnnualTypesLoading(false);
    }
  }, []);

  const previewAnnualReport = async () => {
    if (!selectedProgramType) {
      setAnnualError('Select one of the four program types to preview the annual report.');
      return;
    }

    setAnnualPreviewLoading(true);
    setAnnualError('');
    try {
      const data = await authPost(
        '/api/reports/annual-report/preview',
        buildAnnualReportPreviewPayload({
          startDate: annualStartDate,
          endDate: annualEndDate,
          programType: selectedProgramType
        })
      );
      setAnnualPreview(data);
    } catch (err) {
      console.error('Preview annual report error:', err);
      setAnnualPreview(null);
      setAnnualError(err.message || 'Unable to preview annual report');
    } finally {
      setAnnualPreviewLoading(false);
    }
  };

  useEffect(() => {
    loadAnnualReportTypes();
  }, [loadAnnualReportTypes]);

  useEffect(() => {
    loadReport();
  }, []);

  const downloadAnnualReport = async () => {
    try {
      setAnnualError('');
      const blob = await authPostBlob('/api/reports/annual-report/export.pdf', {
        ...buildAnnualReportPreviewPayload({ startDate: annualStartDate, endDate: annualEndDate, programType: selectedProgramType }),
        manual: { spanishCourses, significantSubstanceAbusePercent }
      });
      const url = URL.createObjectURL(blob); const link = document.createElement('a');
      link.href = url; link.download = 'annual-report.pdf'; link.click(); URL.revokeObjectURL(url);
    } catch (err) { setAnnualError(err.message || 'Unable to export annual report PDF'); }
  };

  const instructorColumns = [
    { field: 'instructor_name', headerName: 'Instructor', flex: 1.5 },
    { field: 'class_count', headerName: 'Classes', flex: 1 }
  ];

  const studentsPerClassColumns = [
    { field: 'class_code', headerName: 'Code', flex: 0.8 },
    { field: 'class_name', headerName: 'Class', flex: 1.5 },
    { field: 'student_count', headerName: 'Students', flex: 1 }
  ];

  const detailColumns = [
    { field: 'class_code', headerName: 'Code', flex: 0.7 },
    { field: 'class_name', headerName: 'Class', flex: 1.4 },
    { field: 'class_date', headerName: 'Start Date', flex: 1, renderCell: (params) => formatDate(params.row.class_date) },
    {
      field: 'class_time',
      headerName: 'Start Time',
      flex: 1,
      renderCell: (params) => formatTimeLabel(params.row.class_time)
    },
    { field: 'end_date', headerName: 'End Date', flex: 1, renderCell: (params) => formatDate(params.row.end_date || params.row.class_date) },
    {
      field: 'end_time',
      headerName: 'End Time',
      flex: 1,
      renderCell: (params) => formatTimeLabel(params.row.end_time)
    },
    { field: 'instructor_name', headerName: 'Instructor', flex: 1.3 },
    { field: 'student_count', headerName: 'Students', flex: 0.8 }
  ];

  const filteredClassDetails = selectedClass
    ? reportData.classDetails.filter((row) => row.class_name === selectedClass)
    : reportData.classDetails;

  const classOptions = [...new Set(reportData.studentsPerClass.map((item) => item.class_name))];

  const {
    columnVisibilityModel: instructorColumnVisibilityModel,
    setColumnVisibilityModel: setInstructorColumnVisibilityModel,
    setFieldVisible: setInstructorFieldVisible,
    showSimpleView: showInstructorSimpleView,
    showFullView: showInstructorFullView,
    resetView: resetInstructorView
  } = useSavedColumnView('reports-instructors', instructorColumns, ['instructor_name', 'class_count']);

  const {
    columnVisibilityModel: studentsColumnVisibilityModel,
    setColumnVisibilityModel: setStudentsColumnVisibilityModel,
    setFieldVisible: setStudentsFieldVisible,
    showSimpleView: showStudentsSimpleView,
    showFullView: showStudentsFullView,
    resetView: resetStudentsView
  } = useSavedColumnView('reports-students-per-class', studentsPerClassColumns, ['class_code', 'class_name', 'student_count']);

  const {
    columnVisibilityModel: detailColumnVisibilityModel,
    setColumnVisibilityModel: setDetailColumnVisibilityModel,
    setFieldVisible: setDetailFieldVisible,
    showSimpleView: showDetailSimpleView,
    showFullView: showDetailFullView,
    resetView: resetDetailView
  } = useSavedColumnView('reports-detail', detailColumns, ['class_code', 'class_name', 'class_date', 'instructor_name', 'student_count']);

  return (
    <Box p={3}>
      <Typography variant="h3" gutterBottom>
        Reports
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={3}>
        Select a date range and review instructor, class, and annual reporting data.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="end">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Start"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="End"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Students per class"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
            >
              <MenuItem value="">All Classes</MenuItem>
              {classOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={loadReport} disabled={loading}>
                Show
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Annual Report
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Select the reporting period and one of the four program types. Course participants counts everyone in that program's classes plus everyone in its holding tank (counted once); successful participants are students with an active class enrollment — records removed or moved to the holding tank do not count as passed. Knowledge increase is the average individual pre-test to post-test percent change.
        </Typography>

        {annualError && <Alert severity="error" sx={{ mb: 2 }}>{annualError}</Alert>}

        <Grid container spacing={2} alignItems="end">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Annual report start"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={annualStartDate}
              onChange={(e) => setAnnualStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Annual report end"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={annualEndDate}
              onChange={(e) => setAnnualEndDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Program type"
              value={selectedProgramType}
              onChange={(e) => {
                setSelectedProgramType(e.target.value);
                setAnnualPreview(null);
              }}
              disabled={annualTypesLoading}
              helperText="All classes of this type and all its instructors for the reporting period."
            >
              <MenuItem value="">
                <em>{annualTypesLoading ? 'Loading program types…' : 'Select a program type'}</em>
              </MenuItem>
              {annualProgramTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={loadAnnualReportTypes} disabled={annualTypesLoading}>
                {annualTypesLoading ? 'Loading types…' : 'Refresh types'}
              </Button>
              <Button
                variant="contained"
                onClick={previewAnnualReport}
                disabled={annualPreviewLoading || !selectedProgramType}
              >
                {annualPreviewLoading ? 'Preparing preview…' : 'Preview'}
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Spanish-language courses"
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={spanishCourses}
              onChange={(e) => setSpanishCourses(e.target.value)}
              helperText="Manual entry; not calculated from course records."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Participants with significant substance-abuse involvement (%)"
              type="number"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              value={significantSubstanceAbusePercent}
              onChange={(e) => setSignificantSubstanceAbusePercent(e.target.value)}
              helperText="Manual entry; this measure is not inferred from student records."
            />
          </Grid>
        </Grid>

        {annualPreview?.metrics && (
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>Preview metrics</Typography>
            <Grid container spacing={2}>
              {[
                ['Courses conducted', annualPreview.metrics.coursesConducted],
                ['Course participants', annualPreview.metrics.courseParticipants],
                ['Successful course participants', annualPreview.metrics.successfulCourseParticipants],
                ['In holding tank only (not passed)', annualPreview.metrics.holdingTankParticipants],
                ['Knowledge increase', annualPreview.metrics.knowledgeIncreasePercent == null ? 'No valid scores' : `${annualPreview.metrics.knowledgeIncreasePercent}%`],
                ['Spanish-language courses', spanishCourses === '' ? 'Not entered' : spanishCourses],
                ['Significant substance-abuse involvement', significantSubstanceAbusePercent === '' ? 'Not entered' : `${significantSubstanceAbusePercent}%`]
              ].map(([label, value]) => (
                <Grid item xs={12} sm={6} md={4} key={label}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
                    <Typography variant="h5">{value}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Typography variant="body2" color="text.secondary" mt={2}>
              Knowledge scores included: {annualPreview.metrics.scoredParticipants || 0}. Excluded records: {annualPreview.metrics.excludedKnowledgeRecords || 0}.
            </Typography>
            <Button sx={{ mt: 2 }} variant="contained" onClick={downloadAnnualReport}>
              Export filled Annual Report PDF
            </Button>
          </Box>
        )}
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Instructor by classes
            </Typography>
            <SavedColumnViewControls
              columns={instructorColumns}
              columnVisibilityModel={instructorColumnVisibilityModel}
              setFieldVisible={setInstructorFieldVisible}
              showSimpleView={showInstructorSimpleView}
              showFullView={showInstructorFullView}
              resetView={resetInstructorView}
              label="Columns"
            />
            <Box sx={{ height: 280, width: '100%' }}>
              <DataGrid
                rows={reportData.instructorByClasses.map((row, i) => ({ id: i + 1, ...row }))}
                columns={instructorColumns}
                hideFooter
                disableRowSelectionOnClick
                loading={loading}
                columnVisibilityModel={instructorColumnVisibilityModel}
                onColumnVisibilityModelChange={setInstructorColumnVisibilityModel}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Students per class
            </Typography>
            <SavedColumnViewControls
              columns={studentsPerClassColumns}
              columnVisibilityModel={studentsColumnVisibilityModel}
              setFieldVisible={setStudentsFieldVisible}
              showSimpleView={showStudentsSimpleView}
              showFullView={showStudentsFullView}
              resetView={resetStudentsView}
              label="Columns"
            />
            <Box sx={{ height: 280, width: '100%' }}>
              <DataGrid
                rows={reportData.studentsPerClass.map((row, i) => ({ id: i + 1, ...row }))}
                columns={studentsPerClassColumns}
                hideFooter
                disableRowSelectionOnClick
                loading={loading}
                columnVisibilityModel={studentsColumnVisibilityModel}
                onColumnVisibilityModelChange={setStudentsColumnVisibilityModel}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Annual
            </Typography>

            <Stack spacing={2} mt={1}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Total Classes</Typography>
                <Typography variant="h4">{reportData.annual.total_classes}</Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Total Student Seats</Typography>
                <Typography variant="h4">{reportData.annual.total_student_seats}</Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Class Types Used</Typography>
                <Typography variant="h4">{reportData.annual.class_types_used}</Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Instructors Used</Typography>
                <Typography variant="h4">{reportData.annual.instructors_used}</Typography>
              </Paper>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Detailed Results
            </Typography>
            <SavedColumnViewControls
              columns={detailColumns}
              columnVisibilityModel={detailColumnVisibilityModel}
              setFieldVisible={setDetailFieldVisible}
              showSimpleView={showDetailSimpleView}
              showFullView={showDetailFullView}
              resetView={resetDetailView}
              label="Detail Columns"
            />
            <Box sx={{ height: 420, width: '100%' }}>
              <DataGrid
                rows={filteredClassDetails.map((row) => ({ ...row, id: row.id }))}
                columns={detailColumns}
                disableRowSelectionOnClick
                loading={loading}
                pageSizeOptions={[10, 25, 50]}
                columnVisibilityModel={detailColumnVisibilityModel}
                onColumnVisibilityModelChange={setDetailColumnVisibilityModel}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}